import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";

import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import EntityTypesSupportedInOutOfFrequency from "@app/types/dashboard/cards/outOfFrequency/EntityTypesSupportedInOutOfFrequency";
import OutOfFrequencyCardConfiguration from "@app/types/dashboard/cards/outOfFrequency/OutOfFrequencyCardConfiguration";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { createEmptyFilterModel } from "@app/util/filters/utils";

import {
  applyOutOfFrequencyDrillDownListViewSettings,
  fetchOutOfFrequencyCardDrillDownData,
  hideOutOfFrequencyDrillDown,
  OutOfFrequencyCardActions,
  showOutOfFrequencyDrillDown,
} from "./actions";

export interface OutOfFrequencyCardState {
  configuration?: OutOfFrequencyCardConfiguration; // also works as a "visible" trigger, visible if set
  loading?: boolean;
  totalFilteredRecords: Record<EntityTypesSupportedInOutOfFrequency, number>;
  totalRecords: Record<EntityTypesSupportedInOutOfFrequency, number>;
  viewStates: Record<EntityTypesSupportedInOutOfFrequency, DrillDownViewState>;
}

export const outOfFrequencyInitialState: OutOfFrequencyCardState = {
  totalFilteredRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInOutOfFrequency, number>
  ),
  totalRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInOutOfFrequency, number>
  ),
  viewStates: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => {
      result[entityType] = {
        columns: [],
        filter: createEmptyFilterModel(),
        range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
        sort: [],
      };
      return result;
    },
    {} as Record<EntityTypesSupportedInOutOfFrequency, DrillDownViewState>
  ),
};

const outOfFrequencyState = createReducer<OutOfFrequencyCardState, OutOfFrequencyCardActions>(
  outOfFrequencyInitialState
)
  .handleAction(showOutOfFrequencyDrillDown.request, (state, { payload }) => ({
    ...state,
    configuration: payload.configuration,
  }))
  .handleAction(showOutOfFrequencyDrillDown.success, (state, { payload }) => ({
    ...state,
    viewStates: payload.viewStates,
  }))
  .handleAction(hideOutOfFrequencyDrillDown, (state) => ({ ...state, configuration: undefined }))
  .handleAction(fetchOutOfFrequencyCardDrillDownData.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(fetchOutOfFrequencyCardDrillDownData.success, (state, { payload }) => ({
    ...state,
    loading: false,
    totalFilteredRecords: {
      ...state.totalFilteredRecords,
      [payload.entityType]: payload.totalFilteredRecords,
    },
    totalRecords: {
      ...state.totalRecords,
      [payload.entityType]: payload.totalRecords,
    },
  }))
  .handleAction(fetchOutOfFrequencyCardDrillDownData.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(applyOutOfFrequencyDrillDownListViewSettings, (state, { payload }) => {
    const viewState = state.viewStates[payload.entityType];
    return {
      ...state,
      viewStates: {
        ...state.viewStates,
        [payload.entityType]: {
          ...viewState,
          columns: payload.columns ?? viewState.columns,
          filter: payload.filter ?? viewState.filter,
          range: payload.range ?? viewState.range,
          sort: payload.sort ?? viewState.sort,
        },
      },
    };
  });

export default outOfFrequencyState;
